import { AchMethodModel, PaymentMethodModel } from '../../../../customer/customer.model';
import { last4FromAccount } from '../../../../freely-pay/utils/payment-method-display';

/**
 * What one saved account looks like on the Collect Payment "which account" step.
 *
 * Kept free of Angular so the rules — which brand a token is, when a card reads as expired —
 * are tested as themselves rather than through a rendered template.
 */
export interface AccountCardView {
  rail: 'CARD' | 'ACH';
  /** CSS modifier and brand discriminator. */
  brandKey: 'visa' | 'mastercard' | 'amex' | 'discover' | 'freely' | 'card' | 'ach';
  brandLabel: string;
  /** FontAwesome classes for the brand mark. Unused for the Freely Card, which has its own image. */
  brandIcon: string;
  last4: string;
  holderName: string;
  /** Credit / Debit / Prepaid, when the token says. */
  fundingLabel?: string;
  /** `MM/YY`, cards only. */
  expiry?: string;
  expired: boolean;
  /** ABA routing number, ACH only. */
  routing?: string;
  /** One sentence naming the account, for screen readers. */
  ariaLabel: string;
  /** Set by the host: the profile refuses this card (blockDebitCards), so it cannot be chosen. */
  notAccepted?: boolean;
}

/**
 * A token is ACH when either field says so. `tokenType` is the authoritative one, but the legacy
 * token shape also stamps `accountType: 'ACH'` on bank rows and not every row carries both.
 */
export function isAchMethod(method: PaymentMethodModel): boolean {
  return method.tokenType === 'ACH' || method.accountType === 'ACH';
}

const BRANDS: Record<string, Pick<AccountCardView, 'brandKey' | 'brandLabel' | 'brandIcon'>> = {
  VISA: { brandKey: 'visa', brandLabel: 'Visa', brandIcon: 'fa-brands fa-cc-visa' },
  VISADEBIT: { brandKey: 'visa', brandLabel: 'Visa', brandIcon: 'fa-brands fa-cc-visa' },
  MC: { brandKey: 'mastercard', brandLabel: 'Mastercard', brandIcon: 'fa-brands fa-cc-mastercard' },
  MCDEBIT: { brandKey: 'mastercard', brandLabel: 'Mastercard', brandIcon: 'fa-brands fa-cc-mastercard' },
  MASTERCARD: { brandKey: 'mastercard', brandLabel: 'Mastercard', brandIcon: 'fa-brands fa-cc-mastercard' },
  AMEX: { brandKey: 'amex', brandLabel: 'American Express', brandIcon: 'fa-brands fa-cc-amex' },
  'AMERICAN EXPRESS': { brandKey: 'amex', brandLabel: 'American Express', brandIcon: 'fa-brands fa-cc-amex' },
  DISC: { brandKey: 'discover', brandLabel: 'Discover', brandIcon: 'fa-brands fa-cc-discover' },
  DISCOVER: { brandKey: 'discover', brandLabel: 'Discover', brandIcon: 'fa-brands fa-cc-discover' },
  FREELY: { brandKey: 'freely', brandLabel: 'Freely Card', brandIcon: '' },
};

const FUNDING_LABELS: Record<string, string> = { CREDIT: 'Credit', DEBIT: 'Debit', PREPAID: 'Prepaid' };

/**
 * Whether an `MMYY` expiration has passed. A card is good through the last day of its month, so
 * `0926` is still valid on 30 Sept 2026 and expired on 1 Oct.
 */
export function isCardExpired(expirationDate: string | undefined, now: Date = new Date()): boolean {
  if (!expirationDate || !/^\d{4}$/.test(expirationDate)) return false;
  const month = Number(expirationDate.slice(0, 2));
  const year = 2000 + Number(expirationDate.slice(2));
  if (month < 1 || month > 12) return false;
  const firstDayAfterExpiry = new Date(year, month, 1);
  return now >= firstDayAfterExpiry;
}

export function describeAccount(method: PaymentMethodModel, now: Date = new Date()): AccountCardView {
  const last4 = last4FromAccount(method.account);
  const holderName = (method.cardHolderName ?? '').trim();

  if (isAchMethod(method)) {
    const routing = (method as AchMethodModel).abaRoute || undefined;
    return {
      rail: 'ACH',
      brandKey: 'ach',
      brandLabel: 'Bank account',
      brandIcon: 'fa-solid fa-building-columns',
      last4,
      holderName,
      routing,
      expired: false,
      ariaLabel: `Bank account ending ${last4 || 'unknown'}${holderName ? `, ${holderName}` : ''}`,
    };
  }

  const rawBrand = (method.cardType || method.accountType || '').trim().toUpperCase();
  const brand = BRANDS[rawBrand] ?? {
    brandKey: 'card' as const,
    brandLabel: rawBrand && rawBrand !== 'CARD' ? rawBrand : 'Card',
    brandIcon: 'fa-solid fa-credit-card',
  };
  const expiry = method.expirationDate && /^\d{4}$/.test(method.expirationDate)
    ? `${method.expirationDate.slice(0, 2)}/${method.expirationDate.slice(2)}`
    : undefined;
  const expired = isCardExpired(method.expirationDate, now);

  return {
    rail: 'CARD',
    ...brand,
    last4,
    holderName,
    fundingLabel: FUNDING_LABELS[(method.cardFunding ?? '').toUpperCase()],
    expiry,
    expired,
    ariaLabel: `${brand.brandLabel} ending ${last4 || 'unknown'}${expired ? ', expired' : ''}`,
  };
}
